/**
 * BookingSummaryDisplay Component
 * 
 * Pure presentational component that displays booking summary items.
 * This is the reusable part - accepts pre-formatted strings as props.
 * 
 * Used inside TicketCard to show bay name, date, time, and duration.
 * Does NOT handle pricing, buttons, or data fetching.
 * 
 * @example
 * ```tsx
 * <TicketCard borderColor="#F4EBFF">
 *   <BookingSummaryDisplay
 *     bayName="Augusta"
 *     date="Friday, 21 November, 2025"
 *     timeRange="10:00 AM - 11:00 AM"
 *     duration="1 Hour"
 *   />
 *   <TicketDivider punch />
 *   <BookingActions ... />
 * </TicketCard>
 * ```
 */

import { SummaryItem } from './SummaryItem';
import { LocationIcon } from '#icons';
import { CalendarIcon } from '#icons';
import { ClockIcon } from '#icons';
import { HourglassIcon } from '#icons';
import { UsersIcon } from '#icons';
import { TagIcon } from '#icons';
import { CrownIcon } from '#icons';
import { CoachIcon } from '#icons';

export interface BookingSummaryDisplayProps {
  /** Title shown above the summary items */
  title?: string;
  /** Instructor name (pre-formatted). When set, renders a Coach pill FIRST — the lesson ticket. */
  instructorName?: string;
  /** Bay/location name (pre-formatted) */
  bayName: string;
  /** Date string (pre-formatted) */
  date: string;
  /** Time range string (pre-formatted, e.g., "10:00 AM - 11:00 AM") */
  timeRange: string;
  /** Duration string (pre-formatted, e.g., "1 Hour") */
  duration: string;
  /**
   * Number of guests on the booking. When > 0, renders a "N Guests" pill.
   * Omit or pass 0 for a solo booking (no pill rendered).
   */
  guestCount?: number;
  /**
   * Flags that this bay carries a surcharge, e.g. `"Bay Premium"`. Renders a pill
   * alongside the others so the higher total has a visible reason rather than
   * looking like a pricing bug.
   *
   * Deliberately a label, not an amount — the priced breakdown lives on the next
   * step, and a per-hour figure sitting beside a per-booking total invites the
   * wrong sum.
   *
   * The caller decides when it applies: charged only to bookers with no plan, and
   * never on a bay restricted to selected plans. Omit otherwise.
   */
  premiumNote?: string;
  /**
   * Flags that peak-hour pricing applied to this booking, e.g. `"Peak Pricing"`.
   * Renders a pill alongside the others.
   *
   * Deliberately absent from the slot picker and only shown once a time is
   * chosen: product does not want a member told which hours are peak while they
   * are still browsing, but a higher total at the point of payment needs a
   * visible reason or it reads as a bug.
   *
   * A label, not an amount, for the same reason as `premiumNote` — the priced
   * breakdown belongs on the confirmation step.
   */
  peakNote?: string;
  /**
   * A promo code the quote APPLIED, e.g. `"SPRING20"`. Renders the code as a
   * pill alongside the others, so a discounted total has its reason in view.
   * The caller passes it only when the server applied it; omit otherwise.
   */
  promoCode?: string;
  /** Additional CSS classes for the container */
  className?: string;
}

/**
 * BookingSummaryDisplay - Pure presentational booking summary
 * 
 * Displays the 4 summary items (bay, date, time, duration) in a responsive layout,
 * plus an optional leading instructor pill for the lesson ticket.
 * - Mobile: Vertical stack
 * - Desktop: Horizontal flex wrap
 */
export function BookingSummaryDisplay({
  title = 'Booking Summary',
  instructorName,
  bayName,
  date,
  timeRange,
  duration,
  guestCount,
  premiumNote,
  peakNote,
  promoCode,
  className = '',
}: BookingSummaryDisplayProps) {
  const hasGuests = typeof guestCount === 'number' && guestCount > 0;
  const guestLabel = hasGuests
    ? `${guestCount} ${guestCount === 1 ? 'Guest' : 'Guests'}`
    : '';
  return (
    <div className={`px-4 py-6 md:p-6 ${className}`}>
      <h2 
        className="mb-6 text-sm text-fg-tertiary"
        data-testid="booking-summary-title"
      >
        {title}
      </h2>

      {/* Mobile Layout (< md) - Vertical Stack */}
      <div 
      className="flex flex-col gap-3 md:hidden" 
      role="list" 
      data-testid="booking-summary-list-mobile"
      >
        {instructorName && (
          <SummaryItem icon={CoachIcon} label={instructorName} srLabel="Instructor" />
        )}
        <SummaryItem icon={LocationIcon} label={bayName} srLabel="Bay" />
        <SummaryItem icon={CalendarIcon} label={date} srLabel="Date" />
        <SummaryItem icon={ClockIcon} label={timeRange} srLabel="Time" />
        <SummaryItem icon={HourglassIcon} label={duration} srLabel="Duration" />
        {hasGuests && (
          <SummaryItem icon={UsersIcon} label={guestLabel} srLabel="Guests" />
        )}
        {premiumNote && (
          <SummaryItem icon={CrownIcon} label={premiumNote} srLabel="Bay premium" />
        )}
        {peakNote && (
          <SummaryItem icon={TagIcon} label={peakNote} srLabel="Peak pricing" />
        )}
        {promoCode && (
          <SummaryItem icon={TagIcon} label={promoCode} srLabel="Promo" />
        )}
      </div>

      {/* Desktop Layout (>= md) - Horizontal */}
      <div 
        className="hidden md:flex md:flex-wrap md:gap-x-6 md:gap-y-3" 
        role="list" 
        data-testid="booking-summary-list-desktop"
      >
        {instructorName && (
          <SummaryItem icon={CoachIcon} label={instructorName} srLabel="Instructor" />
        )}
        <SummaryItem icon={LocationIcon} label={bayName} srLabel="Bay" />
        <SummaryItem icon={CalendarIcon} label={date} srLabel="Date" />
        <SummaryItem icon={ClockIcon} label={timeRange} srLabel="Time" />
        <SummaryItem icon={HourglassIcon} label={duration} srLabel="Duration" />
        {hasGuests && (
          <SummaryItem icon={UsersIcon} label={guestLabel} srLabel="Guests" />
        )}
        {premiumNote && (
          <SummaryItem icon={CrownIcon} label={premiumNote} srLabel="Bay premium" />
        )}
        {peakNote && (
          <SummaryItem icon={TagIcon} label={peakNote} srLabel="Peak pricing" />
        )}
        {promoCode && (
          <SummaryItem icon={TagIcon} label={promoCode} srLabel="Promo" />
        )}
      </div>
    </div>
  );
}
